import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import ContactSelector from '../ContactSelector/ContactSelector';
import { resetContactInput, setContactInputExpand, setSelectedContactId, setRemarks, setTriggerFocusCompanyNameInput, setContactInput, setCompanyName, setSubAcct, setKeywords, setCountry, setInterested, setSizeFocus, setCompanyType, setEmails } from './reducers/ContactInputReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import ContactNameModel from '../ContactSelector/models/ContactNameModel';
import { contactSelectorSelector, setContactSearchResults } from '../ContactSelector/reducers/ContactSelectorReducer';
import { contactInputSelector } from './reducers/ContactInputReducer';
import ContactApi from './api/ContactApi';
import ContactModel from './models/ContactModel';
import { ContactInputModel } from './models/ContactInputModel';
import { CompanySizeModel, CountryModel } from './models/ContactStaticResourcesModel';

const ContactInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const contactInputViewData = useSelector(contactInputSelector);
    const contactSelectorData = useSelector(contactSelectorSelector);

    useEffect(() => {
        if (contactInputViewData.triggerSave > 0) {
            save();
        }
    }, [contactInputViewData.triggerSave]);

    useEffect(() => {
        if (contactInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [contactInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetContactInput());
        dispatch(setSelectedContactId(""));
        dispatch(setTriggerFocusCompanyNameInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (contactInputViewData.selectedContactId.length > 0) {
            selectContactById(contactInputViewData.selectedContactId);
        }
    }, [contactInputViewData.selectedContactId])

    const selectContactById = (id: string) => {
        dispatch(showLoading(true));
        ContactApi.GetById({
            id: id
        }).then((response: BaseResponse<ContactModel>) => {
            if (response.success) {
                let contactInput = Utility.CloneObject(contactInputViewData.contactInput) as ContactInputModel;

                contactInput.id = response.data.id;
                contactInput.companyName = response.data.companyName;
                contactInput.subAcct = response.data.subAcct;
                contactInput.keywords = response.data.keywords;
                contactInput.companyType = response.data.companyType;
                contactInput.country = response.data.country;
                contactInput.emails = response.data.emails;
                contactInput.interested = response.data.interested;
                contactInput.sizeFocus = response.data.sizeFocus;
                contactInput.remarks = response.data.remarks ? response.data.remarks : "";

                setContactForContactSelector(response.data);

                dispatch(setContactInput(contactInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const setContactForContactSelector = (data: ContactModel) => {
        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];

        searchResults.push({
            id: data.id,
            companyName: data.companyName
        } as ContactNameModel);

        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
            return item.id;
        });
        dispatch(setContactSearchResults(searchResults));
    }

    const save = () => {
        try {
            validate();

            ContactApi.Save(contactInputViewData.contactInput)
                .then((response: BaseResponse<ContactModel>) => {
                    if (response.success) {
                        if (contactInputViewData.contactInput.id.length === 0) {
                            Utility.Alert.Success("Contact added successful");
                        } else {
                            Utility.Alert.Success("Contact updated successful");
                        }

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (contactInputViewData.contactInput.companyName.length === 0) {
            throw "Company name is required";
        }
    }

    const getCountry = () => {
        let country = _.find(contactInputViewData.staticResources.countries, (country) => {
            return country.code === contactInputViewData.contactInput.country
        })

        return country ? country : null;
    }

    const getSizeFocus = () => {
        let sizeFocuses = contactInputViewData.contactInput.sizeFocus ? contactInputViewData.contactInput.sizeFocus.split(",") : [];
        let size = _.filter(contactInputViewData.staticResources.sizeFocus, (companySize) => {
            return _.indexOf(sizeFocuses, companySize.key) > -1;
        })

        return size ? size : [];
    }

    return (
        <>
            {
                contactInputViewData.staticResources.companyTypes.length > 0 &&
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    open={contactInputViewData.ContactInputExpand}
                    onOpenChange={(isOpen: boolean) => {
                        dispatch(setContactInputExpand(isOpen));
                    }}
                >
                    <Grid container>
                        <Grid item lg={3} xs={12}>
                            <Grid container className='cargo-input-component'>
                                <Grid xs={10} lg={9}>
                                    <ContactSelector
                                        label='Company Name'
                                        selectedContactId={contactInputViewData.contactInput.companyName}
                                        useFocusMode={true}
                                        focusValue={contactInputViewData.triggerFocusCompanyNameInput}
                                        useFreeText={true}
                                        currentContactName={contactInputViewData.contactInput.companyName}
                                        onContactIdChange={(value: string) => {
                                            dispatch(setSelectedContactId(value));
                                        }}
                                        onContactNameChange={(value: string) => {
                                            dispatch(setCompanyName(value.toUpperCase()));
                                        }}
                                    >
                                    </ContactSelector>
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Tooltip title="Reset (Alt + R)">
                                        <Button
                                            className="clear-input-button"
                                            tabIndex={-1}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                refreshInput();
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Subs acct"
                                value={contactInputViewData.contactInput.subAcct}
                                onChange={(event) => {
                                    dispatch(setSubAcct(event.target.value));
                                }}
                            >
                            </TextField>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Keywords"
                                value={contactInputViewData.contactInput.keywords}
                                onChange={(event) => {
                                    dispatch(setKeywords(event.target.value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <Autocomplete
                                className='cargo-input-component'
                                multiple={true}
                                options={contactInputViewData.staticResources.companyTypes}
                                renderInput={(params) => <TextField {...params} label="Company Type" />}
                                value={!contactInputViewData.contactInput.companyType || contactInputViewData.contactInput.companyType.length === 0 ? [] : contactInputViewData.contactInput.companyType.split(',')}
                                onChange={(event: React.SyntheticEvent, value: string[] | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setCompanyType(value ? value.join(',') : ""));
                                }}
                            />
                            <Autocomplete
                                className='cargo-input-component'
                                options={contactInputViewData.staticResources.countries}
                                getOptionLabel={(option: CountryModel) => {
                                    return option.name + "(" + option.code + ")"
                                }}
                                renderInput={(params) => <TextField {...params} label="Country" />}
                                value={getCountry()}
                                onChange={(event: React.SyntheticEvent, value: CountryModel | null, reason: string, details?: AutocompleteChangeDetails<CountryModel> | undefined) => {
                                    dispatch(setCountry(value ? value.code : ""));
                                }}
                            />
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <Autocomplete
                                className='cargo-input-component'
                                options={contactInputViewData.staticResources.interesteds}
                                renderInput={(params) => <TextField {...params} label="Interest" />}
                                value={contactInputViewData.contactInput.interested}
                                onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setInterested(value ? value : ""));
                                }}
                            />
                            <Autocomplete
                                className='cargo-input-component'
                                multiple={true}
                                options={contactInputViewData.staticResources.sizeFocus}
                                getOptionLabel={(option: CompanySizeModel) => {
                                    return option.key + " [" + option.value + "]";
                                }}
                                renderInput={(params) => <TextField {...params} label="Size Focus" />}
                                value={getSizeFocus()}
                                onChange={(event: React.SyntheticEvent, value: CompanySizeModel[] | null, reason: string, details?: AutocompleteChangeDetails<CompanySizeModel> | undefined) => {
                                    let sizeFocus = _.map(value, (item) => {
                                        return item.key;
                                    })
                                    dispatch(setSizeFocus(value ? sizeFocus.join(",") : ""));
                                }}
                            />
                        </Grid>
                        <Grid item lg={4} xs={12}>
                            <TextField
                                fullWidth
                                label="Emails"
                                className='cargo-input-component'
                                value={contactInputViewData.contactInput.emails}
                                onChange={(event) => {
                                    dispatch(setEmails(event.target.value ? event.target.value : ""));
                                }}
                            >
                            </TextField>
                            <TextField
                                label="Remarks"
                                multiline
                                fullWidth
                                rows={3}
                                value={contactInputViewData.contactInput.remarks}
                                onChange={(event) => {
                                    dispatch(setRemarks(event.target.value ? event.target.value : ""));
                                }}
                                className='cargo-input-component tonnage-input-full-tc'
                            />
                        </Grid>
                        <Grid item lg={1} xs={12}>
                            <Tooltip title="Alt + S">
                                <Button
                                    onClick={save}
                                    variant="outlined"
                                >
                                    Save (s)
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default ContactInput;